<template>
  <div class="prompting">
    <!-- Header -->
    <div class="border-b border-black bg-white">
      <div class="max-w-4xl mx-auto px-4 py-4">
        <div class="flex items-center justify-between">
          <div class="flex items-center space-x-3">
            <div class="w-8 h-8 bg-black rounded-lg flex items-center justify-center shadow-lg shadow-black/50">
              <span class="text-white font-bold text-sm">AI</span>
            </div>
            <h1 class="text-xl font-semibold text-black">AI Assistant</h1>
          </div>
          <div class="flex items-center space-x-2">
            <button class="px-3 py-1.5 text-sm bg-gray-100 text-black border border-black rounded-lg hover:bg-gray-200 transition-all duration-200">
              New Chat
            </button>
            <button class="px-3 py-1.5 text-sm bg-gray-100 text-black border border-black rounded-lg hover:bg-gray-200 transition-all duration-200">
              Settings
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="max-w-4xl mx-auto px-4 py-6 bg-white min-h-screen">
      <!-- Chat Messages -->
      <div class="space-y-4 mb-6" style="min-height: 400px;">
        <!-- Welcome Message -->
        <div v-if="history.length === 0 && !response" class="text-center py-12">
          <div class="w-16 h-16 bg-black rounded-full mx-auto mb-4 flex items-center justify-center shadow-lg shadow-black/50">
            <span class="text-white text-2xl">AI</span>
          </div>
          <h2 class="text-2xl font-semibold text-black mb-2">Hello! How can I help you today?</h2>
          <p class="text-gray-600">Ask me anything, from creative writing to coding help</p>
        </div>

        <!-- Conversation History -->
        <div v-for="(item, index) in history" :key="index" class="space-y-4">
          <!-- User Message -->
          <div class="flex justify-end">
            <div class="max-w-2xl">
              <div class="bg-black text-white rounded-2xl rounded-br-sm px-4 py-3 border border-gray-800">
                <p class="text-sm">{{ item.prompt }}</p>
              </div>
              <div class="text-xs text-gray-500 mt-1 text-right">{{ formatTime(item.timestamp) }}</div>
            </div>
          </div>

          <!-- AI Message -->
          <div class="flex justify-start">
            <div class="max-w-2xl">
              <div class="bg-gray-100 text-black rounded-2xl rounded-bl-sm px-4 py-3 border border-black">
                <!-- Check if response contains video -->
                <div v-if="item.response && item.response.includes('video')" class="space-y-3">
                  <p class="text-sm whitespace-pre-wrap">{{ item.response }}</p>
                  <!-- Video Player with dynamic video based on content -->
                  <div class="mt-3">
                    <video 
                      controls 
                      class="w-full max-w-md rounded-lg border border-gray-300"
                      :poster="getVideoPoster(item.response)"
                    >
                      <source :src="getVideoSource(item.response)" type="video/mp4">
                      Your browser does not support the video tag.
                    </video>
                    <p class="text-xs text-gray-500 mt-2">🎬 Demo video - Actual video generation would appear here</p>
                  </div>
                </div>
                <!-- Check if response contains image -->
                <div v-else-if="item.response && item.response.includes('image')" class="space-y-3">
                  <p class="text-sm whitespace-pre-wrap">{{ item.response }}</p>
                  <!-- Image Display -->
                  <div class="mt-3">
                    <img 
                      :src="getImageSource(item.response)" 
                      :alt="getImageAlt(item.response)"
                      class="w-full max-w-md rounded-lg border border-gray-300"
                      @error="handleImageError"
                    >
                    <p class="text-xs text-gray-500 mt-2">🎨 Demo image - Actual generated image would appear here</p>
                  </div>
                </div>
                <!-- Regular text response -->
                <p v-else class="text-sm whitespace-pre-wrap">{{ item.response }}</p>
              </div>
              <div class="text-xs text-gray-500 mt-1">AI • {{ item.model }}</div>
            </div>
          </div>
        </div>

        <!-- Current AI Response -->
        <div v-if="response && !isGenerating" class="flex justify-start">
          <div class="max-w-2xl">
            <div class="bg-gray-100 text-black rounded-2xl rounded-bl-sm px-4 py-3 border border-black">
              <!-- Check if response contains video -->
              <div v-if="response.includes('video')" class="space-y-3">
                <p class="text-sm whitespace-pre-wrap">{{ response }}</p>
                <!-- Video Player with dynamic video based on content -->
                <div class="mt-3">
                  <video 
                    controls 
                    class="w-full max-w-md rounded-lg border border-gray-300"
                    :poster="getVideoPoster(response)"
                  >
                    <source :src="getVideoSource(response)" type="video/mp4">
                    Your browser does not support the video tag.
                  </video>
                  <p class="text-xs text-gray-500 mt-2">🎬 Demo video - Actual video generation would appear here</p>
                </div>
              </div>
              <!-- Check if response contains image -->
              <div v-else-if="response.includes('image')" class="space-y-3">
                <p class="text-sm whitespace-pre-wrap">{{ response }}</p>
                <!-- Image Display -->
                <div class="mt-3">
                  <img 
                    :src="getImageSource(response)" 
                    :alt="getImageAlt(response)"
                    class="w-full max-w-md rounded-lg border border-gray-300"
                    @error="handleImageError"
                  >
                  <p class="text-xs text-gray-500 mt-2">🎨 Demo image - Actual generated image would appear here</p>
                </div>
              </div>
              <!-- Regular text response -->
              <p v-else class="text-sm whitespace-pre-wrap">{{ response }}</p>
            </div>
            <div class="flex items-center space-x-2 mt-2">
              <button @click="copyResponse" class="text-xs text-black hover:text-gray-700">
                Copy
              </button>
              <button @click="regenerateResponse" class="text-xs text-black hover:text-gray-700">
                Regenerate
              </button>
            </div>
          </div>
        </div>

        <!-- Loading State -->
        <div v-if="isGenerating" class="flex justify-start">
          <div class="max-w-2xl">
            <div class="bg-gray-100 text-black rounded-2xl rounded-bl-sm px-4 py-3 border border-black">
              <div class="flex items-center space-x-2">
                <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-black"></div>
                <span class="text-sm">Thinking...</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Input Area -->
      <div class="border-t border-black pt-4">
        <div class="flex items-end space-x-2">
          <div class="flex-1">
            <textarea
              v-model="prompt"
              @keydown.enter.prevent="handleEnter"
              placeholder="Type your message... (Press Enter to send, Shift+Enter for new line)"
              rows="1"
              class="w-full px-4 py-3 bg-gray-100 text-black border border-black rounded-lg focus:outline-none focus:ring-2 focus:ring-black resize-none placeholder-gray-500"
              style="max-height: 200px;"
              :disabled="isGenerating"
            ></textarea>
          </div>
          <button
            @click="generateResponse"
            :disabled="!prompt.trim() || isGenerating"
            class="px-4 py-3 bg-black text-white rounded-lg hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 font-semibold"
          >
            {{ isGenerating ? 'Pause' : 'Send' }}
          </button>
        </div>
        
        <!-- Controls -->
        <div class="flex items-center justify-between mt-3">
          <div class="flex items-center space-x-2">
            <!-- Model Toggle -->
            <button 
              @click="toggleModel"
              class="px-3 py-1.5 text-sm bg-gray-100 text-black border border-black rounded-lg hover:bg-gray-200 transition-all duration-200"
            >
              {{ selectedModel === 'asi1-mini' ? 'ASI:One Mini' : 'ASI:One Fast' }}
            </button>
            
            <!-- Quick Action Buttons -->
            <button 
              @click="setTaskMode('image')"
              class="px-3 py-1.5 text-sm bg-purple-100 text-purple-700 border border-purple-300 rounded-lg hover:bg-purple-200 transition-all duration-200"
              :class="{ 'bg-purple-200 border-purple-500': selectedTask === 'image' }"
            >
              🎨 Image
            </button>
            
            <button 
              @click="setTaskMode('video')"
              class="px-3 py-1.5 text-sm bg-blue-100 text-blue-700 border border-blue-300 rounded-lg hover:bg-blue-200 transition-all duration-200"
              :class="{ 'bg-blue-200 border-blue-500': selectedTask === 'video' }"
            >
              🎬 Video
            </button>
            
            <!-- Task Selection Dropdown -->
            <select v-model="selectedTask" class="text-sm bg-gray-100 text-black border border-black rounded px-2 py-1">
              <option value="general">General</option>
              <option value="writing">Writing</option>
              <option value="coding">Coding</option>
              <option value="analysis">Analysis</option>
              <option value="translation">Translation</option>
              <option value="summarization">Summarization</option>
              <option value="image">🎨 Image</option>
              <option value="video">🎬 Video</option>
            </select>
          </div>
          <div class="flex items-center space-x-2">
            <button @click="clearHistory" class="text-xs text-black hover:text-gray-700">
              Clear chat
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onUnmounted } from 'vue'

const selectedModel = ref('asi1-mini')
const selectedTask = ref('general')
const prompt = ref('')
const response = ref('')
const isGenerating = ref(false)
const history = ref([])
const typingTimeout = ref(null)

const settings = ref({
  includeContext: true,
  creativeMode: false,
  temperature: '0.7'
})

const taskTypes = ref([
  {
    id: 'general',
    name: 'General',
    description: 'General assistance'
  },
  {
    id: 'writing',
    name: 'Writing',
    description: 'Creative writing'
  },
  {
    id: 'coding',
    name: 'Coding',
    description: 'Code generation'
  },
  {
    id: 'analysis',
    name: 'Analysis',
    description: 'Data analysis'
  },
  {
    id: 'translation',
    name: 'Translation',
    description: 'Language translation'
  },
  {
    id: 'summarization',
    name: 'Summarization',
    description: 'Text summarization'
  },
  {
    id: 'image',
    name: 'Image',
    description: 'ASI:One Image Generation'
  },
  {
    id: 'video',
    name: 'Video',
    description: 'Video Generation'
  }
])

const templates = ref([
  {
    id: 'blog-post',
    name: 'Blog Post',
    description: 'Write a blog post',
    prompt: 'Write a blog post about [topic] with an engaging introduction and key takeaways.'
  },
  {
    id: 'email',
    name: 'Email',
    description: 'Professional email',
    prompt: 'Write a professional email to [recipient] about [subject].'
  },
  {
    id: 'code-review',
    name: 'Code Review',
    description: 'Review code',
    prompt: 'Review this code and suggest improvements:\n\n[code here]'
  },
  {
    id: 'summary',
    name: 'Summary',
    description: 'Summarize text',
    prompt: 'Summarize this text: [text to summarize]'
  },
  {
    id: 'translate',
    name: 'Translation',
    description: 'Translate text',
    prompt: 'Translate [text] from [language] to [language].'
  }
])

const generateResponse = async () => {
  if (!prompt.value.trim() || isGenerating.value) return

  isGenerating.value = true

  try {
    // Call backend API - use environment variable or default to deployed backend
    const apiUrl = import.meta.env.VITE_API_URL || 'https://ai-assistant-backend.theworld888a.workers.dev'
    const response = await fetch(`${apiUrl}/api/chat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        prompt: prompt.value,
        model: selectedModel.value
      })
    })

    if (!response.ok) {
      throw new Error(`Backend API error: ${response.status}`)
    }

    const data = await response.json()
    response.value = data.response
    
    // Add to history
    history.value.unshift({
      prompt: prompt.value,
      response: response.value,
      model: data.model,
      task: data.taskType,
      timestamp: new Date()
    })
    
    // Clear prompt after sending
    prompt.value = ''
    
  } catch (error) {
    console.error('Backend API Error:', error)
    response.value = `Sorry, I encountered an error: ${error.message}. Please try again.`
  } finally {
    isGenerating.value = false
  }
}

// Handle Enter key
const handleEnter = (event) => {
  // Only send on Enter (not Shift+Enter for new lines)
  if (event.key === 'Enter' && !event.shiftKey) {
    event.preventDefault()
    
    // Clear any existing timeout
    if (typingTimeout.value) {
      clearTimeout(typingTimeout.value)
    }
    
    // Add a delay to ensure user is done typing
    typingTimeout.value = setTimeout(() => {
      if (prompt.value.trim() && !isGenerating.value) {
        generateResponse()
      }
    }, 500) // Increased delay to 500ms
  }
}

// Toggle Model
const toggleModel = () => {
  selectedModel.value = selectedModel.value === 'asi1-mini' ? 'asi1-fast' : 'asi1-mini'
}

// Set Task Mode
const setTaskMode = (mode) => {
  selectedTask.value = mode
  // Update placeholder based on task
  if (mode === 'image') {
    const textarea = document.querySelector('textarea')
    if (textarea) {
      textarea.placeholder = 'Describe the image you want to generate... (Press Enter to send)'
    }
  } else if (mode === 'video') {
    const textarea = document.querySelector('textarea')
    if (textarea) {
      textarea.placeholder = 'Describe the video you want to create... (Press Enter to send)'
    }
  } else {
    const textarea = document.querySelector('textarea')
    if (textarea) {
      textarea.placeholder = 'Type your message... (Press Enter to send, Shift+Enter for new line)'
    }
  }
}

const getSystemPrompt = () => {
  const prompts = {
    general: 'You are a helpful AI assistant. Provide clear, accurate, and helpful responses.',
    writing: 'You are a professional writer. Create well-structured, engaging, and grammatically correct content.',
    coding: 'You are an expert programmer. Provide clean, efficient, and well-commented code solutions.',
    analysis: 'You are a data analyst. Provide insightful analysis with clear conclusions and actionable recommendations.',
    translation: 'You are a professional translator. Provide accurate translations that preserve meaning and cultural context.',
    summarization: 'You are an expert at summarization. Create concise summaries that capture the most important information.'
  }
  
  return prompts[selectedTask.value] || prompts.general
}

const regenerateResponse = async () => {
  await generateResponse()
}

const copyResponse = () => {
  navigator.clipboard.writeText(response.value)
  // You could add a toast notification here
}

const clearPrompt = () => {
  prompt.value = ''
  response.value = ''
}

const savePrompt = () => {
  // Save to localStorage or backend
  const savedPrompts = JSON.parse(localStorage.getItem('savedPrompts') || '[]')
  savedPrompts.push({
    prompt: prompt.value,
    model: selectedModel.value,
    task: selectedTask.value,
    settings: { ...settings.value },
    timestamp: new Date()
  })
  localStorage.setItem('savedPrompts', JSON.stringify(savedPrompts))
  // You could add a success notification here
}

const useTemplate = (template) => {
  prompt.value = template.prompt
}

const clearHistory = () => {
  history.value = []
}

const formatTime = (date) => {
  return new Date(date).toLocaleString()
}

// Get image source based on content
const getImageSource = (response) => {
  const lowerResponse = response.toLowerCase()
  
  // Check if response contains a real image URL from ASI:One API
  const urlMatch = response.match(/https?:\/\/[^\s]+\.(jpg|jpeg|png|gif|webp)/i);
  if (urlMatch) {
    return urlMatch[0]; // Return the real image URL
  }
  
  // Fallback to demo images if no real URL found
  const timestamp = Date.now()
  
  // Different image seeds based on content
  if (lowerResponse.includes('mountain') || lowerResponse.includes('landscape')) {
    return `https://picsum.photos/seed/mountain-${timestamp}/400/300.jpg`
  } else if (lowerResponse.includes('sunset') || lowerResponse.includes('sunrise')) {
    return `https://picsum.photos/seed/sunset-${timestamp}/400/300.jpg`
  } else if (lowerResponse.includes('ocean') || lowerResponse.includes('sea') || lowerResponse.includes('beach')) {
    return `https://picsum.photos/seed/ocean-${timestamp}/400/300.jpg`
  } else if (lowerResponse.includes('forest') || lowerResponse.includes('tree')) {
    return `https://picsum.photos/seed/forest-${timestamp}/400/300.jpg`
  } else if (lowerResponse.includes('city') || lowerResponse.includes('urban')) {
    return `https://picsum.photos/seed/city-${timestamp}/400/300.jpg`
  } else if (lowerResponse.includes('flower') || lowerResponse.includes('garden')) {
    return `https://picsum.photos/seed/flower-${timestamp}/400/300.jpg`
  } else if (lowerResponse.includes('animal') || lowerResponse.includes('pet')) {
    return `https://picsum.photos/seed/animal-${timestamp}/400/300.jpg`
  } else if (lowerResponse.includes('space') || lowerResponse.includes('galaxy')) {
    return `https://picsum.photos/seed/space-${timestamp}/400/300.jpg`
  } else {
    return `https://picsum.photos/seed/silver-surface-${timestamp}/400/300.jpg`
  }
}

// Get image alt text
const getImageAlt = (response) => {
  const lowerResponse = response.toLowerCase()
  
  if (lowerResponse.includes('mountain')) return 'Generated mountain landscape image'
  if (lowerResponse.includes('sunset')) return 'Generated sunset image'
  if (lowerResponse.includes('ocean')) return 'Generated ocean scene image'
  if (lowerResponse.includes('forest')) return 'Generated forest image'
  if (lowerResponse.includes('city')) return 'Generated city image'
  if (lowerResponse.includes('flower')) return 'Generated flower image'
  if (lowerResponse.includes('animal')) return 'Generated animal image'
  if (lowerResponse.includes('space')) return 'Generated space image'
  
  return 'Generated AI image'
}

// Handle image error
const handleImageError = (event) => {
  event.target.src = `https://picsum.photos/seed/fallback-${Date.now()}/400/300.jpg`
}

// Get video source based on content
const getVideoSource = (response) => {
  const videos = [
    'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4',
    'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ElephantsDream.mp4',
    'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerBlazes.mp4',
    'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerEscapes.mp4',
    'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerFun.mp4',
    'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerJoyrides.mp4',
    'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerMeltdowns.mp4',
    'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/Sintel.mp4',
    'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/SubaruOutback.mp4',
    'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/TearsOfSteel.mp4'
  ]
  
  // Select video based on content keywords
  const lowerResponse = response.toLowerCase()
  let videoIndex = 0
  
  if (lowerResponse.includes('car') || lowerResponse.includes('drive') || lowerResponse.includes('city')) {
    videoIndex = 5 // ForBiggerJoyrides - car video
  } else if (lowerResponse.includes('nature') || lowerResponse.includes('animal') || lowerResponse.includes('elephant')) {
    videoIndex = 1 // ElephantsDream
  } else if (lowerResponse.includes('fire') || lowerResponse.includes('blaze')) {
    videoIndex = 2 // ForBiggerBlazes
  } else if (lowerResponse.includes('escape') || lowerResponse.includes('adventure')) {
    videoIndex = 3 // ForBiggerEscapes
  } else if (lowerResponse.includes('fun') || lowerResponse.includes('joy')) {
    videoIndex = 4 // ForBiggerFun
  } else if (lowerResponse.includes('sunset') || lowerResponse.includes('meltdown')) {
    videoIndex = 6 // ForBiggerMeltdowns
  } else if (lowerResponse.includes('fantasy') || lowerResponse.includes('knight') || lowerResponse.includes('sintel')) {
    videoIndex = 7 // Sintel
  } else if (lowerResponse.includes('ocean') || lowerResponse.includes('water') || lowerResponse.includes('steel')) {
    videoIndex = 9 // TearsOfSteel
  } else {
    // Random video for other requests
    videoIndex = Math.floor(Math.random() * videos.length)
  }
  
  return videos[videoIndex]
}

// Get video poster based on content
const getVideoPoster = (response) => {
  const lowerResponse = response.toLowerCase()
  
  if (lowerResponse.includes('car') || lowerResponse.includes('city')) {
    return 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="300" viewBox="0 0 400 300"%3E%3Crect width="400" height="300" fill="%231F2937"/%3E%3Ctext x="200" y="150" text-anchor="middle" fill="white" font-family="Arial" font-size="18"%3E🚗 City Drive Video%3C/text%3E%3C/svg%3E'
  } else if (lowerResponse.includes('nature') || lowerResponse.includes('animal')) {
    return 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="300" viewBox="0 0 400 300"%3E%3Crect width="400" height="300" fill="%23065F46"/%3E%3Ctext x="200" y="150" text-anchor="middle" fill="white" font-family="Arial" font-size="18"%3E🌿 Nature Video%3C/text%3E%3C/svg%3E'
  } else if (lowerResponse.includes('sunset')) {
    return 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="300" viewBox="0 0 400 300"%3E%3Cdefs%3E%3ClinearGradient id="sunset" x1="0%25" y1="0%25" x2="0%25" y2="100%25"%3E%3Cstop offset="0%25" style="stop-color:%23F97316"/%3E%3Cstop offset="100%25" style="stop-color:%237C3AED"/%3E%3C/linearGradient%3E%3C/defs%3E%3Crect width="400" height="300" fill="url(%23sunset)"/%3E%3Ctext x="200" y="150" text-anchor="middle" fill="white" font-family="Arial" font-size="18"%3E🌅 Sunset Video%3C/text%3E%3C/svg%3E'
  } else {
    return 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="300" viewBox="0 0 400 300"%3E%3Crect width="400" height="300" fill="%234B5563"/%3E%3Ctext x="200" y="150" text-anchor="middle" fill="white" font-family="Arial" font-size="18"%3E🎬 Video Preview%3C/text%3E%3C/svg%3E'
  }
}

// Cleanup timeout on component unmount
onUnmounted(() => {
  if (typingTimeout.value) {
    clearTimeout(typingTimeout.value)
  }
})
</script>

<style scoped>
.prompting {
  min-height: calc(100vh - 200px);
}

/* Custom animations */
@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.animate-spin {
  animation: spin 1s linear infinite;
}

/* Prose styling for AI responses */
.prose {
  color: #374151;
  line-height: 1.75;
}

.prose p {
  margin-bottom: 1em;
}

.prose ul, .prose ol {
  margin-bottom: 1em;
  padding-left: 1.5em;
}

.prose li {
  margin-bottom: 0.5em;
}

.prose code {
  background-color: #f3f4f6;
  padding: 0.125rem 0.25rem;
  border-radius: 0.25rem;
  font-size: 0.875em;
}

.prose pre {
  background-color: #1f2937;
  color: #f9fafb;
  padding: 1rem;
  border-radius: 0.5rem;
  overflow-x: auto;
}

.prose pre code {
  background-color: transparent;
  padding: 0;
  color: inherit;
}
</style>
